/**
 * L'ÉCHÉANCIER, CÔTÉ ÉCRAN — lot 97, 18/09/2026.
 *
 * Ce fichier ne dessine rien et n'appelle rien. Il transforme les pièces
 * rendues par l'API en lignes datées (une par paiement prévu), les filtre,
 * les regroupe par semaine et calcule les tuiles et la courbe. Les DATES
 * PRÉVUES viennent de `@stafa/core` — la même règle que le BFR et la
 * trésorerie liront.
 */

import {
  ajouterJours,
  centimes,
  ecartJours,
  lundiDe,
  partsDe,
  semaineIso,
  type Part,
  type PieceAPrevoir,
} from '@stafa/core';

export type CoteEcheancier = 'client' | 'fournisseur';

export interface PaiementPrevu {
  readonly montant: string | null;
  readonly date: string;
}

/** Une pièce telle que `GET /analyse/echeancier/:cote` la rend. */
export interface PieceEcheancier {
  readonly pennylane_id: string;
  readonly numero: string | null;
  readonly libelle: string | null;
  readonly tiers: { readonly cle: string; readonly nom: string };
  readonly activite_id: string | null;
  readonly date_piece: string | null;
  readonly echeance: string | null;
  readonly montant_ttc: string;
  readonly reste_du: string;
  readonly prelevement: boolean;
  readonly mode_paiement: string | null;
  readonly retard: {
    readonly jours: number | null;
    readonly retenus: number;
    readonly exclus: number;
  };
  readonly paiements: readonly PaiementPrevu[];
  readonly saisie: { readonly par: string; readonly le: string } | null;
  readonly facture_id: string | null;
  readonly promesse: { readonly date: string; readonly note: string | null } | null;
  readonly cedee_le: string | null;
}

export interface Activite {
  readonly id: string;
  readonly nom: string;
  readonly actif: boolean;
}

export interface EcheancierLu {
  readonly cote: CoteEcheancier;
  readonly aujourdhui: string;
  readonly lu_le: string | null;
  readonly activites: readonly Activite[];
  readonly pieces: readonly PieceEcheancier[];
}

/** Une ligne du tableau : une part de pièce, à sa date. */
export interface LigneEcheancier {
  readonly cle: string;
  readonly piece: PieceEcheancier;
  readonly part: Part;
}

export function versPieceAPrevoir(p: PieceEcheancier): PieceAPrevoir {
  return {
    echeance: p.echeance,
    date_piece: p.date_piece,
    reste_centimes: centimes(p.reste_du),
    prelevement: p.prelevement,
    retard_jours: p.retard.jours,
    tranches:
      p.paiements.length === 0
        ? null
        : p.paiements.map((x) => ({
            montant_centimes: x.montant === null ? null : centimes(x.montant),
            date: x.date,
          })),
  };
}

export function lignesDe(
  pieces: readonly PieceEcheancier[],
  aujourdhui: string,
): LigneEcheancier[] {
  return pieces.flatMap((piece) =>
    partsDe(versPieceAPrevoir(piece), aujourdhui).map((part) => ({
      cle: `${piece.pennylane_id}#${String(part.rang)}`,
      piece,
      part,
    })),
  );
}

export type Quand =
  'tout' | 'retard' | 'revoir' | 'calculee' | 'saisie' | 'semaine' | '4sem';

export interface Filtres {
  readonly texte: string;
  /** Une activité, `sans` (aucune), ou `tout`. */
  readonly activite: string;
  readonly tiers: string;
  readonly quand: Quand;
  /** Le lundi d'une semaine choisie sur la courbe, ou nul. */
  readonly semaine: string | null;
}

export const SANS_FILTRE: Filtres = {
  texte: '',
  activite: 'tout',
  tiers: 'tout',
  quand: 'tout',
  semaine: null,
};

export function estEchue(p: PieceEcheancier, aujourdhui: string): boolean {
  return p.echeance !== null && p.echeance < aujourdhui;
}

/**
 * LE JOUR D'AUJOURD'HUI DANS LE FUSEAU DE LA PERSONNE — ajouté le 18/09/2026
 * après un écran blanc en production.
 *
 * L'ÉCRAN PRENAIT LE JOUR DE L'API, et se rabattait sur la chaîne vide tant
 * que la réponse n'était pas arrivée. Or `filtrer` calcule le lundi de la
 * semaine sans condition : `lundiDe('')` traverse `ajouterJours`, arrive à
 * `new Date(NaN).toISOString()` et LÈVE. Une exception pendant le rendu vide
 * l'application entière — écran blanc, et même le bouton retour perdu.
 *
 * Un repli sur une VRAIE date supprime la classe entière du défaut : plus
 * aucune date vide ne circule, quel que soit le calcul ajouté plus tard. Le
 * jour du navigateur ne sert que le temps du chargement, sur des listes
 * vides ; dès que l'API répond, c'est son jour à elle qui compte.
 *
 * PAS `toISOString()`, qui rendrait le jour à Greenwich : le 1er janvier à
 * 00 h 30 en France, il donnerait le 31 décembre. Même fonction que dans
 * `ot.ts` et `affectation.ts`, répétée plutôt qu'importée, comme elles.
 */
export function aujourdhuiLocal(maintenant: Date = new Date()): string {
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

function sansAccents(t: string): string {
  return t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

export function filtrer(
  lignes: readonly LigneEcheancier[],
  f: Filtres,
  aujourdhui: string,
): LigneEcheancier[] {
  const cherche = sansAccents(f.texte.trim());
  const lundi = lundiDe(aujourdhui);
  const finSemaine = ajouterJours(lundi, 6);
  const fin4 = ajouterJours(lundi, 27);
  return lignes.filter(({ piece, part }) => {
    if (cherche !== '') {
      const botte = sansAccents(
        [piece.numero ?? '', piece.tiers.nom, piece.libelle ?? ''].join(' '),
      );
      if (!botte.includes(cherche)) return false;
    }
    if (f.activite !== 'tout') {
      if (
        f.activite === 'sans'
          ? piece.activite_id !== null
          : piece.activite_id !== f.activite
      ) {
        return false;
      }
    }
    if (f.tiers !== 'tout' && piece.tiers.cle !== f.tiers) return false;
    if (f.semaine !== null && lundiDe(part.date) !== f.semaine) return false;
    switch (f.quand) {
      case 'retard':
        return estEchue(piece, aujourdhui);
      case 'revoir':
        return part.a_revoir;
      case 'calculee':
        return part.origine === 'calculee';
      case 'saisie':
        return part.origine === 'saisie' || part.origine === 'echeancier';
      case 'semaine':
        return part.date <= finSemaine;
      case '4sem':
        return part.date <= fin4;
      default:
        return true;
    }
  });
}

export interface Tuiles {
  readonly reste: number;
  readonly pieces: number;
  readonly echues: { readonly montant: number; readonly n: number };
  readonly revoir: { readonly montant: number; readonly n: number };
  /** Part du montant dont la date n'a jamais été saisie, en %. */
  readonly partCalculee: number;
  readonly montantCalcule: number;
}

export function tuiles(lignes: readonly LigneEcheancier[], aujourdhui: string): Tuiles {
  const pieces = new Map<string, PieceEcheancier>();
  for (const l of lignes) pieces.set(l.piece.pennylane_id, l.piece);
  const reste = lignes.reduce((s, l) => s + l.part.montant_centimes, 0);
  const echues = [...pieces.values()].filter((p) => estEchue(p, aujourdhui));
  const revoir = lignes.filter((l) => l.part.a_revoir);
  const calc = lignes.filter((l) => l.part.origine === 'calculee');
  const montantCalcule = calc.reduce((s, l) => s + l.part.montant_centimes, 0);
  return {
    reste,
    pieces: pieces.size,
    echues: {
      n: echues.length,
      montant: echues.reduce((s, p) => s + centimes(p.reste_du), 0),
    },
    revoir: {
      n: revoir.length,
      montant: revoir.reduce((s, l) => s + l.part.montant_centimes, 0),
    },
    partCalculee: reste === 0 ? 0 : Math.round((montantCalcule / reste) * 100),
    montantCalcule,
  };
}

export interface Semaine {
  readonly lundi: string;
  readonly dimanche: string;
  readonly numero: number;
}

/** Les `n` semaines à partir de celle d'aujourd'hui. */
export function semainesAVenir(aujourdhui: string, n = 7): Semaine[] {
  const premier = lundiDe(aujourdhui);
  return Array.from({ length: n }, (_, i) => {
    const lundi = ajouterJours(premier, 7 * i);
    return { lundi, dimanche: ajouterJours(lundi, 6), numero: semaineIso(lundi) };
  });
}

export interface PointCourbe {
  readonly semaine: Semaine;
  readonly tout: number;
  readonly saisies: number;
  readonly n: number;
}

/**
 * LA COURBE : la somme des parts par semaine prévue. `saisies` ne compte que
 * les dates saisies et les prélèvements (maquette v2). Le reste — au-delà de
 * la dernière semaine — est rendu à part : la somme des points et de
 * `au_dela` égale le total filtré (contrôle `__verifier` de la maquette).
 */
export function courbe(
  lignes: readonly LigneEcheancier[],
  semaines: readonly Semaine[],
): { points: PointCourbe[]; au_dela: number; n_au_dela: number } {
  const points = semaines.map((s) => {
    const dans = lignes.filter(
      (l) => l.part.date >= s.lundi && l.part.date <= s.dimanche,
    );
    return {
      semaine: s,
      tout: dans.reduce((t, l) => t + l.part.montant_centimes, 0),
      saisies: dans
        .filter((l) => l.part.origine !== 'calculee')
        .reduce((t, l) => t + l.part.montant_centimes, 0),
      n: dans.length,
    };
  });
  const derniere = semaines[semaines.length - 1];
  const apres =
    derniere === undefined ? [] : lignes.filter((l) => l.part.date > derniere.dimanche);
  return {
    points,
    au_dela: apres.reduce((t, l) => t + l.part.montant_centimes, 0),
    n_au_dela: apres.length,
  };
}

export interface GroupeSemaine {
  readonly lundi: string;
  readonly numero: number;
  readonly total: number;
  readonly lignes: readonly LigneEcheancier[];
}

export function grouperParSemaine(lignes: readonly LigneEcheancier[]): GroupeSemaine[] {
  const carte = new Map<string, LigneEcheancier[]>();
  for (const l of lignes) {
    const lundi = lundiDe(l.part.date);
    const liste = carte.get(lundi) ?? [];
    liste.push(l);
    carte.set(lundi, liste);
  }
  return [...carte.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([lundi, liste]) => ({
      lundi,
      numero: semaineIso(lundi),
      total: liste.reduce((t, l) => t + l.part.montant_centimes, 0),
      lignes: liste,
    }));
}

export type CleTri =
  'prevue' | 'piece' | 'tiers' | 'date' | 'echeance' | 'reste' | 'retard';

export function trier(
  lignes: readonly LigneEcheancier[],
  cle: CleTri,
  sens: 1 | -1,
): LigneEcheancier[] {
  const valeur = (l: LigneEcheancier): string | number => {
    switch (cle) {
      case 'piece':
        return l.piece.numero ?? '';
      case 'tiers':
        return sansAccents(l.piece.tiers.nom);
      case 'date':
        return l.piece.date_piece ?? '';
      case 'echeance':
        return l.piece.echeance ?? '';
      case 'reste':
        return l.part.montant_centimes;
      case 'retard':
        return l.piece.retard.jours ?? -9999;
      default:
        return `${l.part.date}${l.piece.echeance ?? ''}${l.piece.numero ?? ''}`;
    }
  };
  return [...lignes].sort((a, b) => {
    const va = valeur(a);
    const vb = valeur(b);
    return (va < vb ? -1 : va > vb ? 1 : 0) * sens;
  });
}

/** Les étiquettes d'une ligne, dans l'ordre d'affichage. */
export function etiquettes(l: LigneEcheancier, aujourdhui: string): string[] {
  const e: string[] = [];
  const p = l.piece;
  if (l.part.origine === 'prelevement') e.push('prélèvement');
  if (p.cedee_le !== null) e.push('factor');
  if (l.part.nombre > 1) e.push(`${String(l.part.rang + 1)}/${String(l.part.nombre)}`);
  if (centimes(p.reste_du) < centimes(p.montant_ttc)) e.push('partiel');
  if (estEchue(p, aujourdhui) && p.echeance !== null) {
    e.push(`échue depuis ${String(ecartJours(p.echeance, aujourdhui))} j`);
  }
  if (l.part.a_revoir) e.push('à revoir');
  return e;
}

/** « échéance + 12 j », « prélèvement », « saisie par Mohamed ». */
export function origineEnMots(l: LigneEcheancier): string {
  switch (l.part.origine) {
    case 'prelevement':
      return 'prélèvement, à l’échéance';
    case 'saisie':
      return l.piece.saisie === null ? 'saisie' : `saisie par ${l.piece.saisie.par}`;
    case 'echeancier':
      return `paiement ${String(l.part.rang + 1)} sur ${String(l.part.nombre)}`;
    default: {
      // PLUS DE « + N j » DEPUIS LE LOT 110 : la date calculée est l'échéance
      // et rien d'autre. Garder le retard habituel dans la phrase aurait fait
      // annoncer un décalage que le calcul n'applique plus.
      return l.piece.echeance === null ? 'calculée : date de pièce' : 'à l’échéance';
    }
  }
}

/** Les tiers présents, triés par nom, pour la liste de filtre. */
export function tiersDe(
  pieces: readonly PieceEcheancier[],
): { cle: string; nom: string }[] {
  const carte = new Map<string, string>();
  for (const p of pieces) carte.set(p.tiers.cle, p.tiers.nom);
  return [...carte.entries()]
    .map(([cle, nom]) => ({ cle, nom }))
    .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

const FORMAT_EUROS = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});
const FORMAT_EUROS_CENTIMES = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

export function euros(c: number, avecCentimes = false): string {
  return (avecCentimes ? FORMAT_EUROS_CENTIMES : FORMAT_EUROS).format(c / 100);
}

export function dateFr(iso: string | null): string {
  return iso === null
    ? '—'
    : `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

export function dateCourte(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/**
 * LE CONTRÔLE DU DÉCOUPAGE, pour le dire AVANT l'envoi : ce qui manque ou
 * dépasse, en centimes. L'API refait le même contrôle et fait foi.
 */
export function resteADecouper(montants: readonly string[], reste_du: string): number {
  return (
    centimes(reste_du) - montants.reduce((s, m) => s + centimes(m.replace(',', '.')), 0)
  );
}
